import { TZDateMini } from '@date-fns/tz';
import {
  useCallback,
  useMemo,
  useRef,
  type RefObject,
  ClipboardEventHandler,
  KeyboardEventHandler as ReactKeyboardEventHandler,
} from 'react';
import { useIntl } from 'react-intl';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import { isFullTimeframe } from '../../../core/time/helpers.js';
import { useDateTimeRangePickerStore } from '../../../core/time/timeframeStore/useTimeframeStore.js';
import { focusDateTimeInput } from '../../date-time-picker/utils/focusDateTimeInput.js';
import {
  captureUndoSnapshot,
  isContentModifyingKey,
  normalizeISOInput,
  parseDateOnlyToISO,
  parseTimeOnlyToISO,
} from '../helpers.js';
import {
  getExpressionCursorOffset,
  restoreExpressionCursorOffset,
} from '../selection-helpers.js';
import type {
  DateTimeInputRef,
  DateTimeRangePickerProps,
  UndoSnapshot,
} from '../types.js';

/**
 * Restores spinbutton DOM text from a snapshot by driving each handler directly.
 */
const restoreSpinButtons = (
  ref: RefObject<DateTimeInputRef | null>,
  spinButtonTexts: Map<Intl.DateTimeFormatPart['type'], string>,
): void => {
  spinButtonTexts.forEach((text, type) => {
    const handler = ref.current?.spinButtonHandlers.get(type) ?? null;
    if (!handler) {
      return;
    }
    if (text === '') {
      handler.clearValue(true);
    } else if (handler.type === 'number') {
      handler.applyText(text, true);
    } else {
      handler.searchValue(text[0] ?? '', true);
    }
  });
};

/**
 * Restores a single from/to snapshot side (spin buttons or expression input).
 */
const restoreSnapshot = (
  snapshot: UndoSnapshot,
  ref: RefObject<DateTimeInputRef | null>,
): void => {
  if (snapshot.valueType === 'iso8601') {
    restoreSpinButtons(ref, snapshot.spinButtonTexts);
  } else if (ref.current?.expressionInputRef) {
    const invalidExpresssion = !snapshot.value?.absoluteDate;
    if (invalidExpresssion) {
      restoreSpinButtons(ref, snapshot.spinButtonTexts);
    }
    ref.current.expressionInputRef.textContent = snapshot.expressionText;
  }
};

interface UseDateTimeRangePickerProps
  extends Pick<DateTimeRangePickerProps, 'placeholder' | 'readOnly'> {
  dateTimeFromRef: RefObject<DateTimeInputRef | null>;
  dateTimeToRef: RefObject<DateTimeInputRef | null>;
}

/**
 * A hook that provides the default functionality for the DateTimeRange picker
 * @internal
 */
export const useDateTimeRangePicker = (props: UseDateTimeRangePickerProps) => {
  const { placeholder, readOnly, dateTimeFromRef, dateTimeToRef } = props;
  const { formatMessage } = useIntl();
  const value = useDateTimeRangePickerStore(
    (state) => state.dateTimeRangePickerTimeframe,
  );
  const setValue = useDateTimeRangePickerStore(
    (state) => state.setDateTimeRangePickerTimeframe,
  );

  const pasteUndoSnapshotRef = useRef<{
    from: UndoSnapshot;
    to: UndoSnapshot;
    focusTarget: Element | null;
    expressionCursorOffset?: number;
  } | null>(null);

  const localizedPlaceholder = useMemo(
    () => ({
      from:
        placeholder?.from ??
        formatMessage({
          defaultMessage: 'Start time',
          id: 'gnKOHMjpyzk6hNcF',
          description: 'Start of timeframe',
        }),
      to:
        placeholder?.to ??
        formatMessage({
          defaultMessage: 'End time',
          id: 'sGxf5yRVhyC8Fpo/',
          description: 'End of timeframe',
        }),
    }),
    [formatMessage, placeholder?.from, placeholder?.to],
  );

  // for non null values, put the from/to value as `<TimeValue>/<TimeValue>` into the clipboard
  const onCopyCapture: ClipboardEventHandler = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isFullTimeframe(value)) {
        return;
      }

      navigator.clipboard
        .writeText(`${value.from.value}/${value.to.value}`)
        .catch(() => {
          // For now, we silently ignore clipboard write errors.
        });
    },
    [value],
  );

  // clipboard paste handler. apply clipboard content if formatted correctly
  const onPasteCapture: ClipboardEventHandler = useCallback(
    (e) => {
      if (readOnly) {
        return;
      }
      const data = e.clipboardData?.getData('text/plain');
      if (!data) {
        e.preventDefault();
        return;
      }

      if (!dateTimeFromRef.current || !dateTimeToRef.current) {
        return;
      }

      // Capture cursor offset to be able to restore it in case of an undo.
      const focusTarget = document.activeElement;
      const isExpressionInputFocused =
        focusTarget === dateTimeFromRef.current?.expressionInputRef ||
        focusTarget === dateTimeToRef.current?.expressionInputRef;
      const focusedExpression = isExpressionInputFocused ? focusTarget : null;
      const expressionCursorOffset = focusedExpression
        ? getExpressionCursorOffset(focusedExpression)
        : undefined;

      // Capture undo snapshots for any paste operation (range or single-input).
      const fromValueType =
        (dateTimeFromRef.current?.element?.getAttribute('data-valuetype') as
          | 'expression'
          | 'iso8601') ?? 'expression';
      const toValueType =
        (dateTimeToRef.current?.element?.getAttribute('data-valuetype') as
          | 'expression'
          | 'iso8601') ?? 'expression';
      const fromSnapshot = captureUndoSnapshot(
        dateTimeFromRef.current.spinButtonHandlers,
        dateTimeFromRef.current.expressionInputRef,
        value?.from ?? null,
        fromValueType,
      );
      const toSnapshot = captureUndoSnapshot(
        dateTimeToRef.current.spinButtonHandlers,
        dateTimeToRef.current.expressionInputRef,
        value?.to ?? null,
        toValueType,
      );

      // Detect range format. When the clipboard contains '→', one or both sides
      // are in locale format or have placeholder tokens (e.g. '25/MM/2024 14:30→…').
      // When the clipboard contains only '/', both sides should be ISO-formatted.
      const trimmed = data.trim();
      const hasArrowSep = trimmed.includes('→');
      const parts = hasArrowSep ? trimmed.split('→') : trimmed.split('/');
      const [nextFrom, nextTo] = parts;

      if (nextFrom && nextTo) {
        if (hasArrowSep) {
          // Mixed range: at least one side is locale format or has placeholder tokens.
          // Delegate each part to the individual input's paste handler which already
          // knows how to apply partial/locale values and respect placeholder positions.
          pasteUndoSnapshotRef.current = {
            from: fromSnapshot,
            to: toSnapshot,
            focusTarget,
            expressionCursorOffset,
          };
          dateTimeFromRef.current?.pasteText(nextFrom, true);
          dateTimeToRef.current?.pasteText(nextTo, true);
          e.preventDefault();
          e.stopPropagation();
          // After applying both values, move focus to the first empty spinbutton
          // starting from `from`, falling back to `to`. Uses the same helper
          // that all other focus-routing paths in the range picker use.
          requestAnimationFrame(() => {
            focusDateTimeInput(null, dateTimeFromRef, dateTimeToRef);
          });
          return;
        }

        // All-ISO range: both sides must parse as valid time values.
        // Guard parts.length === 2 so that a single locale-format value whose
        // date delimiter is '/' (e.g. '25/MM/2024') is not mistaken for a range.
        if (parts.length === 2) {
          const relativeDate = TZDateMini.tz(getTimezone()).getTime();
          const parsedFrom =
            parseDateOnlyToISO(nextFrom) ??
            parseTimeOnlyToISO(nextFrom) ??
            normalizeISOInput(nextFrom);
          const parsedTo =
            parseDateOnlyToISO(nextTo, 'endOfDay') ??
            parseTimeOnlyToISO(nextTo) ??
            normalizeISOInput(nextTo);

          const validFrom = parseTimeAsTimeValue(parsedFrom, relativeDate);
          const validTo = parseTimeAsTimeValue(parsedTo, relativeDate);

          if (validFrom && validTo) {
            pasteUndoSnapshotRef.current = {
              from: fromSnapshot,
              to: toSnapshot,
              focusTarget,
            };
            dateTimeFromRef.current?.pasteText(nextFrom, true);
            dateTimeToRef.current?.pasteText(nextTo, true);
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
      }

      pasteUndoSnapshotRef.current = {
        from: fromSnapshot,
        to: toSnapshot,
        focusTarget,
        expressionCursorOffset,
      };
    },
    [readOnly, value, dateTimeFromRef, dateTimeToRef],
  );

  /**
   * Ctrl+Z / Cmd+Z handler for the range picker container.
   * Intercepts when a paste undo snapshot is pending and restores the affected input(s).
   * Clears the snapshot after undoing or when any content modifying key is pressed to prevent stale undos.
   */
  const onUndoPasteKeyDown: ReactKeyboardEventHandler = useCallback(
    (event) => {
      const isCtrlZ = (event.ctrlKey || event.metaKey) && event.key === 'z';

      if (isCtrlZ && pasteUndoSnapshotRef.current) {
        const snapshot = pasteUndoSnapshotRef.current;
        pasteUndoSnapshotRef.current = null;

        event.preventDefault();
        event.stopPropagation();

        restoreSnapshot(snapshot.from, dateTimeFromRef);
        restoreSnapshot(snapshot.to, dateTimeToRef);

        // Update the store with the original values so the hidden inputs
        // and any controlled consumers reflect the undo.
        const fromValue = snapshot.from.value ?? undefined;
        const toValue = snapshot.to.value ?? undefined;
        setValue(
          fromValue || toValue ? { from: fromValue, to: toValue } : null,
        );

        // Restore focus to the element that was focused before the paste
        if (
          snapshot.focusTarget instanceof HTMLElement &&
          snapshot.focusTarget !== document.activeElement
        ) {
          snapshot.focusTarget.focus();
        }

        // Restore cursor place in expression input
        if (
          snapshot.expressionCursorOffset !== undefined &&
          snapshot.focusTarget instanceof HTMLSpanElement
        ) {
          restoreExpressionCursorOffset(
            snapshot.focusTarget,
            snapshot.expressionCursorOffset,
          );
        }
      } else if (pasteUndoSnapshotRef.current && isContentModifyingKey(event)) {
        pasteUndoSnapshotRef.current = null;
      }
    },
    [dateTimeFromRef, dateTimeToRef, setValue],
  );

  return {
    placeholder: localizedPlaceholder,
    onCopyCapture,
    onPasteCapture,
    onUndoPasteKeyDown,
  };
};
